<script setup>
import { ref, reactive, watch, onMounted } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';

const props = defineProps({
  visible: { type: Boolean, required: true },
  note: { type: Object, default: null },
  noteTypes: { type: Array, default: () => [] },
  providers: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:visible', 'updated']);

const show = ref(props.visible);
const toast = useToast();
const loading = ref(false);

const form = reactive({
  note: '',
  start: null,
  end: null,
  isBlocked: false,
  providerUuid: '',
  typeUuid: '',
});

const initForm = () => {
  if (!props.note) return;
  
  // Same as EditBookingModal: replace space with T so browser parses as local time, not UTC
  // Normalize to naive local 'YYYY-MM-DDTHH:MM' - avoids UTC shift
  const parseAsLocal = (str) => {
    if (!str) return null;
    if (str instanceof Date) return str;
    // Strip 'Z' and everything after seconds, ensure 'T' separator
    let normalized = String(str).replace(' ', 'T').split('.')[0].replace('Z', '');
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  };

  form.note = props.note.note || '';
  form.start = parseAsLocal(props.note.start_datetime);
  form.end = parseAsLocal(props.note.end_datetime);
  form.isBlocked = !!props.note.is_time_blocked;
  
  // Resolve Provider UUID
  const pUuid = props.note.provider?.uuid || props.note.provider_uuid;
  if (pUuid) {
    form.providerUuid = pUuid;
  } else if (props.note.provider_id) {
    const p = props.providers.find(p => String(p.id) === String(props.note.provider_id));
    form.providerUuid = p ? p.uuid : '';
  } else {
    form.providerUuid = '';
  }

  // Resolve Type UUID
  const tUuid = props.note.type?.uuid || props.note.note_type_uuid;
  if (tUuid) {
    form.typeUuid = tUuid;
  } else if (props.note.note_type_id) {
    const t = props.noteTypes.find(t => String(t.id) === String(props.note.note_type_id));
    form.typeUuid = t ? t.uuid : (props.noteTypes?.length > 0 ? props.noteTypes[0].uuid : '');
  } else {
    form.typeUuid = props.noteTypes?.length > 0 ? props.noteTypes[0].uuid : '';
  }
};

watch(
    () => [props.visible, props.note?.uuid || props.note?.id],
    (newVal, oldVal) => {
        const [isVisible, uuid] = newVal;
        const [oldVisible, oldUuid] = oldVal || [false, null];
        
        show.value = isVisible;
        if (isVisible && (uuid !== oldUuid || !oldVisible)) {
            initForm();
        }
    },
    { immediate: true }
);

watch(show, (v) => {
  emit('update:visible', v);
});

const extractError = (e, defaultMsg = 'Vorgang fehlgeschlagen') => {
    const data = e.response?.data;
    if (data?.errors) {
        const first = Object.values(data.errors).flat()[0];
        if (first) return first;
    }
    return data?.message || e.message || defaultMsg;
};

const toLocalDatetime = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const updateNote = async () => {
  if (!form.start || !form.end || !form.typeUuid) {
    toast.add({ severity: 'warn', summary: 'Warnung', detail: 'Bitte füllen Sie alle Pflichtfelder aus.', life: 3000 });
    return;
  }
  loading.value = true;
  try {
    const payload = {
      note: form.note,
      start_datetime: toLocalDatetime(form.start),
      end_datetime: toLocalDatetime(form.end),
      is_time_blocked: form.isBlocked,
      provider_id: form.providerUuid,
      note_type_id: form.typeUuid,
    };
    
    const uuid = props.note.uuid || props.note.id;
    await axios.patch(`/ajax/admin/notes/${uuid}`, payload);
    
    toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Notiz erfolgreich aktualisiert', life: 3000 });
    emit('updated');
    show.value = false;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(e, 'Aktualisierung fehlgeschlagen'), life: 5000 });
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <Dialog 
    v-model:visible="show" 
    modal 
    header="Notiz Bearbeiten" 
    :pt="{
      header: { class: '!p-5 !border-b !border-gray-100 !items-center' },
      title: { class: '!text-base !font-extrabold !text-gray-900 !uppercase !tracking-tight' }
    }"
  >
    <div class="space-y-4">
      <!-- Start / End -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-2">
          <label class="font-bold text-gray-500 text-[10px] uppercase tracking-widest">Start *</label>
          <Calendar 
            v-model="form.start" 
            showTime 
            :stepMinute="15"
            hourFormat="24" 
            dateFormat="dd.mm.yy" 
            class="w-full"
            inputClass="p-3 border border-gray-200 rounded-xl bg-white text-indigo-600 font-bold focus:ring-2 focus:ring-black outline-none text-[13px]"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="font-bold text-gray-500 text-[10px] uppercase tracking-widest">Ende *</label>
          <Calendar 
            v-model="form.end" 
            showTime 
            :stepMinute="15"
            hourFormat="24" 
            dateFormat="dd.mm.yy" 
            class="w-full"
            inputClass="p-3 border border-gray-200 rounded-xl bg-white text-indigo-600 font-bold focus:ring-2 focus:ring-black outline-none text-[13px]"
          />
        </div>
      </div>

      <!-- Blocked Status -->
      <div class="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 rounded-xl">
        <Checkbox 
          id="is_blocked_edit" 
          v-model="form.isBlocked" 
          :binary="true" 
          class="cursor-pointer"
        />
        <label for="is_blocked_edit" class="text-sm font-bold text-gray-700 cursor-pointer">Zeit blockieren</label>
      </div>

      <!-- Note Text -->
      <div class="flex flex-col gap-2">
        <label class="font-bold text-gray-500 text-[10px] uppercase tracking-widest">Notiz</label>
        <Textarea 
          v-model="form.note" 
          rows="4" 
          class="w-full !p-3 !border-gray-200 !rounded-xl !bg-white focus:!ring-2 focus:!ring-black outline-none transition-all text-sm"
          placeholder="Geben Sie hier Ihre Notiz ein..."
          autoResize
        />
      </div>

      <!-- Provider Selection -->
      <div class="flex flex-col gap-2">
        <label class="font-bold text-gray-500 text-[10px] uppercase tracking-widest">Mitarbeiter</label>
        <Dropdown 
          v-model="form.providerUuid" 
          :options="providers" 
          optionLabel="name" 
          optionValue="uuid" 
          placeholder="Mitarbeiter wählen"
          class="w-full !rounded-xl !border-gray-200"
          inputClass="!p-3 !text-[13px] !font-medium"
        />
      </div>

      <!-- Note Type Selection -->
      <div class="flex flex-col gap-2">
        <label class="font-bold text-gray-500 text-[10px] uppercase tracking-widest">Notiz-Typ *</label>
        <Dropdown 
          v-model="form.typeUuid" 
          :options="noteTypes" 
          optionLabel="name" 
          optionValue="uuid" 
          placeholder="Typ wählen"
          class="w-full !rounded-xl !border-gray-200"
          inputClass="!p-3 !text-[13px] !font-medium"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value" class="flex items-center gap-2">
              <div 
                class="w-3.5 h-3.5 rounded-full border border-gray-100" 
                :style="{ backgroundColor: noteTypes.find(t => t.uuid === slotProps.value)?.color || '#ddd' }"
              ></div>
              <span>{{ noteTypes.find(t => t.uuid === slotProps.value)?.name }}</span>
            </div>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
          <template #option="slotProps">
            <div class="flex items-center gap-2">
              <div 
                class="w-3.5 h-3.5 rounded-full border border-gray-100" 
                :style="{ backgroundColor: slotProps.option.color || '#ddd' }"
              ></div>
              <span>{{ slotProps.option.name }}</span>
            </div>
          </template>
        </Dropdown>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-between items-center w-full pt-4 border-t mt-4">
        <button 
          type="button"
          @click="show = false" 
          class="text-gray-500 font-bold hover:!bg-sky-50 hover:!text-sky-600 transition-all px-6 py-2 rounded-full text-sm"
        >
          Abbrechen
        </button>
        <Button 
          :loading="loading" 
          class="!bg-black !text-[#FFF053] !border-none px-8 py-3 rounded-xl font-bold shadow-lg flex items-center gap-3" 
          @click="updateNote" 
        >
          <i class="pi pi-save text-lg"></i>
          <span class="uppercase tracking-widest text-sm">Speichern</span>
        </Button>
      </div>
    </template>
  </Dialog>
</template>


